import {pushEvent} from "@/lib/gtmDataLayer"
import type {OnboardingIntentKey} from "@/lib/UserProfile"
import {
    normalizeMarketingLocale,
    parseLocaleFromSearchParams,
    persistUserLocale,
} from "@/i18n/resolveLocale"

const STORAGE_KEY = "braiv_signup_context"
const TRIAL_START_PREFIX = "ga4_trial_start_logged_"
const SIGN_UP_PREFIX = "ga4_sign_up_logged_"

// Accounts created before this rollout cutoff are considered pre-existing and
// do NOT fire sign_up or trial_start -- they already signed up and started
// using the product before we shipped these events. Bumping this will re-fire
// events for users created after the new cutoff, so only change it when the
// event semantics change.
const LIFECYCLE_EVENT_ROLLOUT_CUTOFF_MS = Date.parse("2026-04-25T00:00:00Z")

interface LifecycleUser {
    uid: string
    emailVerified?: boolean
    providerData?: ReadonlyArray<{providerId: string} | null>
    metadata?: {creationTime?: string | null}
}

function isAccountOlderThanRolloutCutoff(user: LifecycleUser): boolean {
    const created = user.metadata?.creationTime ? Date.parse(user.metadata.creationTime) : NaN
    if (isNaN(created)) return false
    return created < LIFECYCLE_EVENT_ROLLOUT_CUTOFF_MS
}

function resolveSignUpMethod(user: LifecycleUser): string {
    const providerId = user.providerData?.[0]?.providerId || ""
    if (providerId === "google.com") return "google"
    if (providerId === "password") return "email"
    if (providerId === "apple.com") return "apple"
    return providerId || "email"
}

export interface SignupContext {
    from?: string
    /** Matches onboarding UI keys in `pages/onboard.tsx` (maps server-side to braiv-* product intents). */
    intent?: string
    locale?: string
    ref_page?: string
    ft_source?: string
    ft_medium?: string
    ft_campaign?: string
    ft_landing?: string
    target_lang?: string
    video_url?: string
    upload?: string
}

const ONBOARDING_INTENT_VALUES = new Set<string>(["translation", "shorts", "thumbnails", "youtube", "hosted"])

/**
 * Returns a validated onboarding intent from persisted signup context (sessionStorage),
 * set when the user lands on `/signup` with `?intent=…` from marketing CTAs.
 */
export function getStoredOnboardingIntent(): OnboardingIntentKey | null {
    const ctx = getSignupContext()
    const raw = ctx?.intent?.trim().toLowerCase()
    if (!raw || !ONBOARDING_INTENT_VALUES.has(raw)) return null
    return raw as OnboardingIntentKey
}

export function captureSignupContext(): SignupContext | null {
    if (typeof window === "undefined") return null

    const params = new URLSearchParams(window.location.search)
    const ctx: SignupContext = {}
    const keys: (keyof SignupContext)[] = [
        "from",
        "intent",
        "ref_page",
        "ft_source",
        "ft_medium",
        "ft_campaign",
        "ft_landing",
        "target_lang",
        "video_url",
        "upload",
    ]

    for (const k of keys) {
        const v = params.get(k as string)
        if (v) (ctx as any)[k] = v
    }

    const localeParam = parseLocaleFromSearchParams(params)
    if (localeParam) {
        const normalizedLocale = normalizeMarketingLocale(localeParam)
        if (normalizedLocale) {
            ctx.locale = normalizedLocale
            persistUserLocale(normalizedLocale)
        }
    }

    try {
        const existing = window.sessionStorage.getItem(STORAGE_KEY)
        if (existing) {
            const prev = JSON.parse(existing) as SignupContext
            const merged: SignupContext = {...prev, ...ctx}
            window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(merged))
            return merged
        }
        if (Object.keys(ctx).length > 0) {
            window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(ctx))
        }
    } catch {
        // ignore storage failures (private mode, etc.)
    }

    if (ctx.ft_source || ctx.ft_medium || ctx.ft_campaign || ctx.ft_landing) {
        pushEvent("first_touch_captured", {
            first_touch_source: ctx.ft_source || "",
            first_touch_medium: ctx.ft_medium || "",
            first_touch_campaign: ctx.ft_campaign || "",
            first_touch_landing_page: ctx.ft_landing || "",
        })
    }

    return ctx
}

export function getSignupContext(): SignupContext | null {
    if (typeof window === "undefined") return null
    try {
        const raw = window.sessionStorage.getItem(STORAGE_KEY)
        if (!raw) return null
        return JSON.parse(raw) as SignupContext
    } catch {
        return null
    }
}

export function clearSignupContext(): void {
    if (typeof window === "undefined") return
    try {
        window.sessionStorage.removeItem(STORAGE_KEY)
    } catch {
        // ignore
    }
}

// Fires sign_up exactly once per UID per browser for verified accounts only.
// Called from the global BillingAnalyticsTracker on the user's first
// authenticated session where emailVerified is true. For email signups this
// means the event fires after the user clicks their verification link; for
// Google/Apple OAuth it fires on first authenticated render (since those
// providers mark the email verified on creation).
// Accounts created before LIFECYCLE_EVENT_ROLLOUT_CUTOFF_MS are treated as
// pre-existing users and the flag is written without firing, to prevent a
// one-off spike of sign_up events for the existing user base.
export function pushSignUpOnce(user: LifecycleUser): void {
    if (typeof window === "undefined" || !user?.uid) return
    if (user.emailVerified === false) return
    const key = `${SIGN_UP_PREFIX}${user.uid}`
    let alreadyLogged = false
    try {
        alreadyLogged = window.localStorage.getItem(key) === "1"
    } catch {
        return
    }
    if (alreadyLogged) return

    if (isAccountOlderThanRolloutCutoff(user)) {
        try {
            window.localStorage.setItem(key, "1")
        } catch {
            // ignore
        }
        return
    }

    const ctx = getSignupContext() || {}
    pushEvent("sign_up", {
        method: resolveSignUpMethod(user),
        signup_source: ctx.from || "",
        user_locale: ctx.locale || (typeof document !== "undefined" ? document.documentElement.lang : "") || "",
        ref_page: ctx.ref_page || "",
        first_touch_source: ctx.ft_source || "",
        first_touch_medium: ctx.ft_medium || "",
        first_touch_campaign: ctx.ft_campaign || "",
    })
    try {
        window.localStorage.setItem(key, "1")
    } catch {
        // ignore storage failures
    }
}

// Fires trial_start exactly once per UID per browser. Called from the global
// BillingAnalyticsTracker when a user is first authenticated (and, for email
// signups, has verified their email). This is the plan's interpretation of
// "user has started using the free trial" -- i.e. they're now in the product.
// Pre-rollout accounts are skipped (flag written without firing) to avoid a
// retroactive burst of trial_start events for existing users.
export function pushTrialStartOnce(user: LifecycleUser): void {
    if (typeof window === "undefined" || !user?.uid) return
    if (user.emailVerified === false) return
    const key = `${TRIAL_START_PREFIX}${user.uid}`
    let alreadyLogged = false
    try {
        alreadyLogged = window.localStorage.getItem(key) === "1"
    } catch {
        return
    }
    if (alreadyLogged) return

    if (isAccountOlderThanRolloutCutoff(user)) {
        try {
            window.localStorage.setItem(key, "1")
        } catch {
            // ignore
        }
        return
    }

    const ctx = getSignupContext() || {}
    pushEvent("trial_start", {
        signup_source: ctx.from || "direct",
        page_locale: ctx.locale || (typeof document !== "undefined" ? document.documentElement.lang : "") || "",
        first_touch_source: ctx.ft_source || "",
        first_touch_medium: ctx.ft_medium || "",
        first_touch_campaign: ctx.ft_campaign || "",
    })
    try {
        window.localStorage.setItem(key, "1")
    } catch {
        // ignore storage failures
    }
}
